import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { FlexBox, FlexBoxProps } from '@lumapps/lumx/react';

import './index.scss';

export interface ArrowSideScrollContainerProps extends React.ComponentPropsWithoutRef<'div'> {
    children: React.ReactNode;
    scrollZoneProps?: FlexBoxProps;
    style: React.CSSProperties;
    arrowLeft?: React.ReactNode;
    arrowRight?: React.ReactNode;
    setScrollZone: (element: HTMLDivElement | null) => void;
    scrollRef: React.Ref<HTMLDivElement>;
}

export const CLASSNAME = 'lumx-arrow-side-scroll-container';

/**
 * Internal component used by `ArrowSideScrollContainer`. Do not use directly unless you want to
 * implement your own arrows.
 */
export const ArrowSideScrollContainer = ({
    children,
    scrollZoneProps,
    style,
    arrowLeft,
    arrowRight,
    setScrollZone,
    scrollRef,
    ...forwardProps
}: ArrowSideScrollContainerProps) => {
    const { block, element } = useClassnames(CLASSNAME);

    return (
        <div {...forwardProps} ref={scrollRef} className={block([forwardProps.className])} style={style}>
            {arrowLeft}
            <FlexBox
                {...scrollZoneProps}
                ref={setScrollZone}
                className={element('scroll', [scrollZoneProps?.className])}
                orientation="horizontal"
                tabIndex={0}
            >
                {children}
            </FlexBox>
            {arrowRight}
        </div>
    );
};

/** Wraps items in the ArrowSideScrollContainer and injects the item className. */
ArrowSideScrollContainer.Item = function Item({ children }: { children: React.ReactElement }) {
    const { element } = useClassnames(CLASSNAME);
    const child = React.Children.only(children);
    return React.cloneElement(child, { className: element('item', [child.props.className]) });
};
